import { Subject, takeUntil } from 'rxjs';

import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
  inject,
} from '@angular/core';
import { FormArray, FormControl, FormGroup } from '@angular/forms';

import { CommentsComponent } from '..';
import { DialogService } from '../../services';
import { AuthService } from '../../services/auth/auth.service';

@Component({
  selector: 'phoenix-comments-list',
  template: `
    <div>
      <strong id="comments">{{ title }} </strong>
    </div>
    @if (
      comments.length === 0 &&
      !(addCommentComponent?.isNew && addCommentComponent?.editing)
    ) {
      <section class="my-2">
        <phoenix-no-data></phoenix-no-data>
      </section>
    }
    @if (comments.length !== 0) {
      <div>
        @for (comment of comments.controls; track $index) {
          <phoenix-comments
            [comment]="comment"
            [currentUser]="currentUser"
            (save)="addComment(comment, $index)"
            (remove)="removeComment($index)"
            [disable]="disable"
          >
          </phoenix-comments>
        }
      </div>
    }
    @if (maxComment === undefined || comments.value.length < maxComment) {
      <phoenix-comments
        #addCommentComponent
        [currentUser]="currentUser"
        (save)="addComment($event, null)"
        [disable]="disable"
      >
      </phoenix-comments>
    }
  `,
  standalone: false,
})
export class CommentsListComponent implements OnInit, OnDestroy {
  @ViewChild('addCommentComponent')
  addCommentComponent!: CommentsComponent | null;
  @Output() commentsChange = new EventEmitter<FormArray>();
  @Input() comments!: FormArray<FormGroup>;
  @Input() comment!: FormGroup | null;
  @Input() disable = false;
  @Input() maxComment!: number;
  @Input() title = 'Comments';

  private readonly _authService = inject(AuthService);
  destroy$ = new Subject<void>();

  constructor(readonly dialogService: DialogService) {}

  ngOnInit() {
    if (!this.comments || this.comments instanceof FormGroup) {
      this.comments = new FormArray<any>([]);
      if (this.comment) {
        this.comments.push(this.comment);
      }
    }

    if (!this.disable && this.comments.disabled) {
      this.disable = true;
    }
    this.comments.statusChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(status => {
        if (status === 'DISABLED') {
          this.disable = true;
        } else {
          this.disable = false;
        }
      });
  }

  get currentUser() {
    const user = this._authService.currentUser();
    const displayUser = user?.email ?? user?.displayName;
    return displayUser ?? '';
  }

  removeComment(index: number) {
    this.dialogService
      .confirm('Are you sure you want to delete this comment')
      .afterClosed()
      .subscribe((result: boolean) => {
        if (result) {
          this.comments.removeAt(index);
          this.commentsChange.emit(this.comments);
        }
      });
  }

  addComment(commentData: FormGroup, index: number | null) {
    const commentGroup = new FormGroup({
      name: new FormControl(commentData.get('name')?.value),
      comment: new FormControl(commentData.get('comment')?.value),
      timestamp: new FormControl(commentData.get('timestamp')?.value),
      edited: new FormControl(commentData.get('edited')?.value),
    });

    if (index === null) {
      this.comments.push(commentGroup);
    } else {
      this.comments.setControl(index, commentGroup);
    }

    this.commentsChange.emit(this.comments);
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
